<template>
	<q-dialog v-model="gameStore.openDialogGame" @hide="onHide">
		<q-card class="game-card">
			<!-- Header + step rail -->
			<q-card-section class="header-row">
				<div class="text-h6" style="color: #ff6600">{{ isNew ? 'Create Game' : 'Edit Game' }}</div>
				<div class="step-rail">
					<span
						v-for="(s, i) in steps"
						:key="s.id"
						class="step-chip"
						:class="{ active: s.id === step, done: stepIndex > i }"
						@click="step = s.id"
					>{{ i + 1 }}. {{ s.label }}</span>
				</div>
			</q-card-section>

			<q-separator dark />

			<q-carousel
				v-model="step"
				animated
				transition-prev="slide-right"
				transition-next="slide-left"
				control-color="orange"
				class="wizard-carousel"
				height="100%"
			>
				<!-- STEP: MODULE -->
				<q-carousel-slide name="module" class="slide module-slide">
					<div class="slide-title">Choose a Module</div>
					<div class="slide-sub">Pick a pre-authored adventure to prefill everything, or start custom.</div>
					<div class="module-carousel">
						<button class="mod-nav-btn" type="button" title="Previous" @click="moduleCarouselPrev">&#x25C0;</button>
						<div class="module-card-big" :class="{ selected: isCardChosen(currentModuleCard) }">
							<div class="module-name">{{ currentModuleCard.name }}</div>
							<img v-if="currentModuleCard.image" :src="currentModuleCard.image" class="module-card-big-img" :alt="currentModuleCard.name" />
							<div class="module-card-body">
								<div class="module-tagline">{{ currentModuleCard.tagline }}</div>
								<div class="module-blurb">{{ currentModuleCard.blurb }}</div>
								<div v-if="currentModuleCard.id === 'cold-booting'" class="module-defaults">Races: Human, Cyborg, Android &#183; Sol/Standard content off by default</div>
							</div>
						</div>
						<button class="mod-nav-btn" type="button" title="Next" @click="moduleCarouselNext">&#x25B6;</button>
					</div>
					<div class="module-dots">
						<span v-for="(mod, i) in modulePickList" :key="mod.id || 'custom'" class="module-dot" :class="{ active: i === moduleCarouselIndex, chosen: isCardChosen(mod) }" :title="mod.name" @click="moduleCarouselIndex = i"></span>
					</div>
				</q-carousel-slide>

				<!-- STEP: ADVERTISE -->
				<q-carousel-slide name="advertise" class="slide">
					<div class="slide-title">Advertise the Table</div>
					<q-input v-model="editGame.name" dark filled dense label="Game Name" class="q-mb-sm" />
					<q-input v-model="editGame.description" dark filled dense label="Description" type="textarea" autogrow class="q-mb-sm" />
					<q-input v-model="editGame.imageUrl" dark filled dense label="Image URL" class="q-mb-sm" />
					<q-select
						v-if="isNew"
						v-model="creatorRole" dark filled dense :options="roleOptions"
						label="Your Role" emit-value map-options class="q-mb-sm" style="max-width: 220px"
					/>
					<div class="row q-gutter-sm q-mb-sm">
						<q-input v-model="startDateStr" dark filled dense label="Start Date & Time" type="datetime-local" class="col" />
						<q-input v-model.number="editGame.duration" dark filled dense label="Duration (hrs)" type="number" step="0.5" min="0.5" class="col-3" />
					</div>
					<div class="row q-gutter-sm q-mb-sm">
						<q-select v-model="editGame.frequency" dark filled dense :options="frequencyOptions" label="Frequency" emit-value clearable class="col" />
						<q-input v-model="editGame.cost" dark filled dense label="Cost" class="col" />
						<q-input v-if="isNew" v-model.number="playerSlots" dark filled dense label="Player Slots" type="number" min="0" class="col-3" />
					</div>
					<q-input v-model="editGame.commitment" dark filled dense label="Commitment / Expectations" class="q-mb-sm" />
					<div class="recruit-settings">
						<div class="recruit-row">
							<span class="recruit-label">Recruitment</span>
							<q-btn-toggle
								v-model="editGame.recruitmentVisibility"
								:options="[{ label: 'Open Public', value: 'public' }, { label: 'Friends Only', value: 'friends' }]"
								dense unelevated no-caps toggle-color="orange" color="grey-9" text-color="grey-4"
							/>
						</div>
						<div class="recruit-row">
							<q-toggle v-model="editGame.enforceRestrictions" dense color="orange" label="Enforce game restrictions" />
							<q-icon name="help_outline" size="18px" class="recruit-help">
								<q-tooltip class="sci-fi-tooltip" max-width="300px" anchor="top middle" self="bottom middle">
									When ON, a character that breaks this game's rules (wrong starting Rank, or a Homeworld / Legacy / Origin / module not allowed here) cannot be submitted, and the player is told exactly what fails. When OFF, mismatches only show a warning and the player can still apply.
								</q-tooltip>
							</q-icon>
						</div>
					</div>
				</q-carousel-slide>

				<!-- STEP: OPERATIONAL -->
				<q-carousel-slide name="operational" class="slide">
					<div class="slide-title">Operational Details</div>
					<div class="row q-gutter-sm q-mb-sm">
						<q-input v-model="editGame.system" dark filled dense label="System" class="col" />
						<q-select v-model="editGame.table" dark filled dense :options="tableOptions" label="Table (VTT)" emit-value clearable class="col" />
						<q-select v-model="editGame.comms" dark filled dense :options="commsOptions" label="Comms" emit-value clearable class="col" />
					</div>
					<div class="row q-gutter-sm q-mb-sm">
						<q-input v-model.number="editGame.rank" dark filled dense label="Starting Rank" type="number" min="0" class="col-3" />
						<q-input v-model="editGame.progression" dark filled dense label="Progression (e.g. Ranks 1-4)" class="col" />
						<q-input v-model="editGame.rpRatio" dark filled dense label="RP Ratio (e.g. 60/40 Combat/RP)" class="col" />
					</div>
					<q-input v-model="editGame.utils" dark filled dense label="Utils" class="q-mb-sm" />
					<q-input v-model="editGame.backstoryRules" dark filled dense label="Backstory Rules" class="q-mb-sm" />
					<div class="fg-links-head">Invites <span class="fg-links-note">(shown to accepted players only)</span></div>
					<div class="row q-gutter-sm q-mb-sm">
						<q-input v-model="editGame.fgInviteUrl" dark filled dense label="VTT Invite URL" class="col" />
						<q-input v-model="editGame.fgGameUrl" dark filled dense label="VTT Game URL" class="col" />
					</div>
					<q-input v-model="editGame.commsInviteUrl" dark filled dense label="Comms Invite URL" class="q-mb-sm" />
				</q-carousel-slide>

				<!-- STEP: RACES -->
				<q-carousel-slide name="races" class="slide">
					<div class="slide-title">Allowed Races</div>
					<div class="slide-sub">
						Which origins players may pick.
						<span v-if="currentModule.id">Cold Booting allows Human, Cyborg and Android by default.</span>
						Tick the group header to allow all.
					</div>
					<div v-if="!optionsLoaded" class="loading-note">Loading races…</div>
					<AllowListTree v-else :groups="raceGroups" v-model="editGame.allowedRaces" />
				</q-carousel-slide>

				<!-- STEP: LEGACIES & HOMEWORLDS -->
				<q-carousel-slide name="content" class="slide">
					<div class="slide-title">Legacies & Homeworlds</div>
					<div class="slide-sub">
						Enable a whole group with its header checkbox, or pick individual entries.
						<span v-if="currentModule.id">Sol / Standard content is off by default for this module.</span>
					</div>
					<div v-if="!optionsLoaded" class="loading-note">Loading content…</div>
					<template v-else>
						<div class="content-col-title">Legacies</div>
						<AllowListTree :groups="legacyGroups" v-model="editGame.allowedLegacies" />
						<div class="content-col-title q-mt-md">Homeworlds</div>
						<AllowListTree :groups="homeworldGroups" v-model="editGame.allowedHomeworlds" />
					</template>
				</q-carousel-slide>

				<!-- STEP: PLAYERS (edit only) -->
				<q-carousel-slide v-if="!isNew" name="players" class="slide">
					<div class="slide-title">Players & Recruitment</div>
					<div class="q-mb-md">
						<div class="text-caption q-mb-xs" style="color: #aaa">Invite Players</div>
						<div class="row q-gutter-sm items-end">
							<q-input v-model="inviteAlias" dark filled dense label="Alias" class="col" @keyup.enter="invitePlayer" />
							<q-input v-model="inviteMessage" dark filled dense label="Message (optional)" class="col" />
							<ScifiButton text="INVITE" buttonType="warning" minWidth="80px" @click="invitePlayer" />
						</div>
						<div v-if="inviteError" class="text-negative text-caption q-mt-xs">{{ inviteError }}</div>
					</div>
					<div v-if="editGame.players && editGame.players.length > 0" class="q-mb-md">
						<div class="text-caption q-mb-xs" style="color: #aaa">Players:</div>
						<div class="row q-gutter-sm">
							<q-chip v-for="p in editGame.players" :key="p.userId" color="green-8" text-color="white" icon="person">{{ p.alias }}</q-chip>
						</div>
					</div>
					<div class="q-mt-md">
						<div class="text-caption q-mb-xs" style="color: #aaa">Recruitment</div>
						<div class="row q-gutter-sm items-end">
							<q-input v-model.number="recruitPlayerSlots" dark filled dense label="Player Slots" type="number" min="0" class="col-3" />
							<q-input v-model.number="recruitGmSlots" dark filled dense label="GM Slots" type="number" min="0" class="col-3" />
							<ScifiButton text="RECRUIT" buttonType="warning" minWidth="100px" @click="onRecruit" />
						</div>
						<div v-if="recruitError" class="text-negative text-caption q-mt-xs">{{ recruitError }}</div>
						<div v-if="recruitSuccess" class="text-positive text-caption q-mt-xs">{{ recruitSuccess }}</div>
					</div>
				</q-carousel-slide>

				<!-- STEP: REVIEW -->
				<q-carousel-slide name="review" class="slide">
					<div class="slide-title">Review & {{ isNew ? 'Create' : 'Save' }}</div>
					<div class="review-grid">
						<div class="review-line"><span>Module</span><b>{{ currentModule.name }}</b></div>
						<div class="review-line"><span>Name</span><b>{{ editGame.name || '—' }}</b></div>
						<div class="review-line"><span>When</span><b>{{ startDateStr || 'TBD' }} · {{ editGame.duration }}h · {{ editGame.frequency || 'any' }}</b></div>
						<div class="review-line"><span>Table</span><b>{{ editGame.table || '—' }} · {{ editGame.system }}</b></div>
						<div class="review-line" v-if="isNew"><span>Player Slots</span><b>{{ playerSlots }}</b></div>
						<div class="review-line"><span>Races</span><b>{{ summarise(editGame.allowedRaces, raceGroups) }}</b></div>
						<div class="review-line"><span>Legacies</span><b>{{ summarise(editGame.allowedLegacies, legacyGroups) }}</b></div>
						<div class="review-line"><span>Homeworlds</span><b>{{ summarise(editGame.allowedHomeworlds, homeworldGroups) }}</b></div>
						<div class="review-line"><span>Recruitment</span><b>{{ editGame.recruitmentVisibility === 'friends' ? 'Friends Only' : 'Open Public' }}{{ editGame.enforceRestrictions ? ' · Restrictions enforced' : '' }}</b></div>
						<div class="review-line"><span>VTT Invite URL</span><b class="review-url">{{ editGame.fgInviteUrl || '—' }}</b></div>
						<div class="review-line"><span>VTT Game URL</span><b class="review-url">{{ editGame.fgGameUrl || '—' }}</b></div>
						<div class="review-line"><span>Comms Invite URL</span><b class="review-url">{{ editGame.commsInviteUrl || '—' }}</b></div>
					</div>
					<div v-if="saveError" class="text-negative q-mt-sm">{{ saveError }}</div>
				</q-carousel-slide>
			</q-carousel>

			<q-separator dark />

			<div class="dialog-actions">
				<ScifiButton text="CANCEL" buttonType="normal" @click="gameStore.hideDialogGame()" />
				<div class="nav-right">
					<ScifiButton v-if="!isNew" text="DELETE" confirmText="CONFIRM" buttonType="negative" :loading="deleting" loadingText="DELETING..." @click="onDelete" />
					<ScifiButton v-if="stepIndex > 0" text="BACK" buttonType="normal" minWidth="80px" @click="prev" />
					<ScifiButton v-if="step === 'module'" text="SELECT" buttonType="positive" minWidth="100px" :loading="!optionsLoaded" loadingText="LOADING..." @click="selectModule(currentModuleCard)" />
					<ScifiButton v-else-if="stepIndex < steps.length - 1" text="NEXT" buttonType="warning" minWidth="80px" @click="next" />
					<ScifiButton v-else :text="isNew ? 'CREATE GAME' : 'SAVE GAME'" buttonType="positive" minWidth="120px" :loading="saving" :loadingText="isNew ? 'CREATING...' : 'SAVING...'" @click="onSave" />
				</div>
			</div>
		</q-card>
	</q-dialog>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useGameStore } from '../stores/game';
import { useInviteStore } from '../stores/invite';
import { useUserStore } from '../stores/user';
import { useRecruitmentStore } from '../stores/recruitment';
import { usePropertyStore } from '../stores/property';
import { type Game, defaultGame, type GameFrequency, type GameTable, type GameComms } from '../models/game';
import { PropertyType } from '../models/property';
import { CAMPAIGN_MODULES, CUSTOM_MODULE, getModule, TAG_GROUP_LABELS, type CampaignModule } from '../data/campaignModules';
import { COLD_BOOTING_TAG, SOL_STANDARD_TAG } from '../data/coldBooting';
import type { AllowGroup } from '../models/allowList';
import { Timestamp } from 'firebase/firestore';

const gameStore = useGameStore();
const inviteStore = useInviteStore();
const userStore = useUserStore();
const recruitmentStore = useRecruitmentStore();
const propertyStore = usePropertyStore();

const editGame = ref<Game>(defaultGame());
const startDateStr = ref('');
const step = ref<string>('module');
const playerSlots = ref(4);
const optionsLoaded = ref(false);
const saveError = ref('');
const saving = ref(false);
const deleting = ref(false);

const inviteAlias = ref('');
const inviteMessage = ref('');
const inviteError = ref('');
const recruitPlayerSlots = ref(1);
const recruitGmSlots = ref(0);
const recruitError = ref('');
const recruitSuccess = ref('');

const isNew = computed(() => !editGame.value.id);
const creatorRole = ref<'player' | 'gm'>('gm');

const roleOptions = [ { label: 'GM', value: 'gm' }, { label: 'Player', value: 'player' } ];
const frequencyOptions: GameFrequency[] = ['Weekly', 'Fortnightly', 'Monthly', 'Ad-hoc', 'One Shot'];
const tableOptions: GameTable[] = ['Foundry VTT', 'Roll20', 'Fantasy Grounds', 'Other'];
const commsOptions: GameComms[] = ['Discord', 'TeamSpeak', 'Guilded', 'Mumble'];

const modulePickList = computed<CampaignModule[]>(() => [...CAMPAIGN_MODULES, CUSTOM_MODULE]);
const currentModule = computed(() => getModule(editGame.value.moduleId));

// Module step is a one-card-at-a-time carousel (bigger art).
const moduleCarouselIndex = ref(0);
const moduleChosen = ref(false);
const currentModuleCard = computed<CampaignModule>(
	() => modulePickList.value[moduleCarouselIndex.value] ?? modulePickList.value[0],
);
const isCardChosen = (mod: CampaignModule): boolean =>
	moduleChosen.value && editGame.value.moduleId === mod.id;
const moduleCarouselPrev = () => {
	const n = modulePickList.value.length;
	moduleCarouselIndex.value = (moduleCarouselIndex.value - 1 + n) % n;
};
const moduleCarouselNext = () => {
	const n = modulePickList.value.length;
	moduleCarouselIndex.value = (moduleCarouselIndex.value + 1) % n;
};

const steps = computed(() => {
	const base = [
		{ id: 'module', label: 'Module' },
		{ id: 'advertise', label: 'Advertise' },
		{ id: 'operational', label: 'Details' },
		{ id: 'races', label: 'Races' },
		{ id: 'content', label: 'Legacies & Worlds' },
	];
	if (!isNew.value) base.push({ id: 'players', label: 'Players' });
	base.push({ id: 'review', label: 'Review' });
	return base;
});
const stepIndex = computed(() => Math.max(0, steps.value.findIndex((s) => s.id === step.value)));
const next = () => { const i = stepIndex.value; if (i < steps.value.length - 1) step.value = steps.value[i + 1].id; };
const prev = () => { const i = stepIndex.value; if (i > 0) step.value = steps.value[i - 1].id; };

// ---- Option loading + grouping ---------------------------------------------
const groupKeyOf = (tags?: string[]): string => {
	if (tags?.includes(COLD_BOOTING_TAG)) return COLD_BOOTING_TAG;
	if (tags?.includes(SOL_STANDARD_TAG)) return SOL_STANDARD_TAG;
	return 'other';
};
const GROUP_ORDER = [COLD_BOOTING_TAG, SOL_STANDARD_TAG, 'other'];

const buildGroups = (type: PropertyType): AllowGroup[] => {
	const props = propertyStore.propertiesByType(type)
		.slice()
		.sort((a, b) => a.name.localeCompare(b.name));
	const byGroup: Record<string, AllowGroup> = {};
	for (const p of props) {
		const key = groupKeyOf(p.tags);
		if (!byGroup[key]) byGroup[key] = { key, label: TAG_GROUP_LABELS[key] ?? key, items: [] };
		byGroup[key].items.push({ id: p.id, name: p.name });
	}
	return GROUP_ORDER.filter((k) => byGroup[k]).map((k) => byGroup[k]);
};

// Races have no tag groups; present them all under one header.
const raceGroups = computed<AllowGroup[]>(() => {
	if (!optionsLoaded.value) return [];
	const origins = propertyStore.propertiesByType(PropertyType.origin)
		.slice().sort((a, b) => a.name.localeCompare(b.name));
	return [{ key: 'races', label: 'Races / Origins', items: origins.map((o) => ({ id: o.id, name: o.name })) }];
});
const legacyGroups = computed<AllowGroup[]>(() => (optionsLoaded.value ? buildGroups(PropertyType.legacy) : []));
const homeworldGroups = computed<AllowGroup[]>(() => (optionsLoaded.value ? buildGroups(PropertyType.homeWorld) : []));

const allIds = (type: PropertyType) => propertyStore.propertiesByType(type).map((p) => p.id);

async function ensureOptionsLoaded() {
	if (optionsLoaded.value) return;
	// Only hit Firestore for types the store doesn't already hold — reopening
	// the dialog (or arriving from a page that fetched properties) is instant.
	const types = [PropertyType.origin, PropertyType.legacy, PropertyType.homeWorld];
	await Promise.all(
		types
			.filter((t) => propertyStore.propertiesByType(t).length === 0)
			.map((t) => propertyStore.fetchByType(t)),
	);
	optionsLoaded.value = true;
}

// Apply a module's prefill + gating to the current (new) game.
function applyModule(mod: CampaignModule) {
	editGame.value.moduleId = mod.id;
	// Merge prefill defaults over the game (new games only).
	Object.assign(editGame.value, { ...mod.gameDefaults });
	if (mod.gameDefaults.startTime === undefined) { /* leave startDateStr as-is */ }
	playerSlots.value = mod.recommendedPlayerSlots;

	// Gating. Per list: an empty default means "all allowed" (Custom has all
	// three empty; Cold Booting restricts races + homeworlds but not legacies).
	editGame.value.allowedRaces = mod.defaultAllowedRaces.length
		? [...mod.defaultAllowedRaces] : allIds(PropertyType.origin);
	editGame.value.allowedLegacies = mod.defaultAllowedLegacies.length
		? [...mod.defaultAllowedLegacies] : allIds(PropertyType.legacy);
	editGame.value.allowedHomeworlds = mod.defaultAllowedHomeworlds.length
		? [...mod.defaultAllowedHomeworlds] : allIds(PropertyType.homeWorld);
}

async function selectModule(mod: CampaignModule) {
	await ensureOptionsLoaded();
	applyModule(mod);
	moduleChosen.value = true;
	next();
}

const summarise = (ids: string[], groups: AllowGroup[]): string => {
	const total = groups.reduce((n, g) => n + g.items.length, 0);
	if (total === 0) return '—';
	if (ids.length === 0) return 'none allowed';
	if (ids.length === total) return `all (${total})`;
	return `${ids.length} of ${total}`;
};

// ---- Open / hydrate --------------------------------------------------------
watch(() => gameStore.openDialogGame, async (open) => {
	if (!open) return;
	saveError.value = '';
	const game = gameStore.selectedGame;
	editGame.value = { ...defaultGame(), ...(game || {}) };

	// Start date field
	startDateStr.value = '';
	const st: any = game?.startTime;
	if (st) {
		let d: Date | null = null;
		if (st instanceof Timestamp) d = st.toDate();
		else if (st?._seconds) d = new Date(st._seconds * 1000);
		else if (st?.seconds) d = new Date(st.seconds * 1000);
		else d = new Date(st);
		if (d && !isNaN(d.getTime())) startDateStr.value = d.toISOString().slice(0, 16);
	}

	step.value = isNew.value ? 'module' : 'advertise';
	optionsLoaded.value = false;
	await ensureOptionsLoaded();

	if (isNew.value) {
		// Preset module (from a "Run it for players" launch) or default to custom-all.
		if (editGame.value.moduleId) applyModule(getModule(editGame.value.moduleId));
		else if (editGame.value.allowedRaces.length === 0
			&& editGame.value.allowedLegacies.length === 0
			&& editGame.value.allowedHomeworlds.length === 0) {
			applyModule(CUSTOM_MODULE);
			editGame.value.moduleId = '';
		}
	}

	// Sync the module carousel: snap to the chosen module (existing game or
	// preset launch); otherwise default to the Cold Booting card. Note the
	// Custom module's id is '' — matching moduleId blindly would snap every
	// new game to the Custom card.
	moduleChosen.value = !!(editGame.value.moduleId || (!isNew.value));
	const defaultIdx = Math.max(0, modulePickList.value.findIndex((m) => m.id === 'cold-booting'));
	const presetIdx = moduleChosen.value
		? modulePickList.value.findIndex((m) => m.id === editGame.value.moduleId)
		: -1;
	moduleCarouselIndex.value = presetIdx >= 0 ? presetIdx : defaultIdx;
}, { immediate: true });

function onHide() {
	// reset transient UI
	inviteAlias.value = ''; inviteMessage.value = ''; inviteError.value = '';
	recruitError.value = ''; recruitSuccess.value = '';
}

// ---- Actions ---------------------------------------------------------------
const invitePlayer = async () => {
	inviteError.value = '';
	const alias = inviteAlias.value.trim();
	if (!alias) return;
	if (!editGame.value.id) { inviteError.value = 'Save the game first before inviting players'; return; }
	try {
		await inviteStore.sendGameInvite(editGame.value.id, alias, inviteMessage.value.trim() || undefined);
	} catch (error: any) { inviteError.value = error?.message || 'Failed to send invite'; return; }
	inviteAlias.value = ''; inviteMessage.value = '';
};

const onRecruit = async () => {
	recruitError.value = ''; recruitSuccess.value = '';
	if (!editGame.value.id) return;
	if (recruitPlayerSlots.value <= 0 && recruitGmSlots.value <= 0) { recruitError.value = 'Set at least one slot'; return; }
	try {
		await recruitmentStore.createRecruitment(editGame.value.id, recruitPlayerSlots.value, recruitGmSlots.value);
		recruitSuccess.value = 'Recruitment posted';
	} catch (e: any) { recruitError.value = e?.message || 'Failed to create recruitment'; }
};

const onSave = async () => {
	if (saving.value) return;
	saveError.value = '';
	if (!editGame.value.name?.trim()) { saveError.value = 'Game name is required'; step.value = 'advertise'; return; }
	if (startDateStr.value) editGame.value.startTime = Timestamp.fromDate(new Date(startDateStr.value));

	saving.value = true;
	try {
		if (isNew.value) {
			const { id, createdAt, createdBy, createdByAlias, players, ...gameData } = editGame.value;
			const gameId = await gameStore.createGame(gameData, creatorRole.value);
			if (gameId) {
				editGame.value.id = gameId;
				// Post recruitment so the table is immediately advertisable.
				if (playerSlots.value > 0) {
					try { await recruitmentStore.createRecruitment(gameId, playerSlots.value, 0); }
					catch (e) { console.warn('Recruitment post failed', e); }
				}
			}
		} else {
			const { id, ...fields } = editGame.value;
			await gameStore.updateGame(id, fields);
		}
		gameStore.hideDialogGame();
	} catch (e: any) {
		saveError.value = e?.message || 'Failed to save game';
	} finally {
		saving.value = false;
	}
};

const onDelete = async () => {
	if (!editGame.value.id) { gameStore.hideDialogGame(); return; }
	deleting.value = true;
	try {
		await gameStore.deleteGame(editGame.value.id);
		gameStore.hideDialogGame();
	} finally {
		deleting.value = false;
	}
};
</script>

<style scoped>
.game-card {
	background: #050a0e;
	color: #00ffea;
	border: 2px solid #00ffea;
	box-shadow: 0 0 12px #00ffea20;
	border-radius: 8px;
	width: min(1100px, 94vw);
	height: min(880px, 90vh);
	max-width: 94vw;
	display: flex;
	flex-direction: column;
}
.header-row { display: flex; align-items: center; gap: 16px; flex-wrap: wrap; padding-bottom: 8px; }
.step-rail { display: flex; gap: 6px; flex-wrap: wrap; }
.step-chip {
	font-size: 0.7em; letter-spacing: 0.5px; color: #567;
	border: 1px solid #234; border-radius: 12px; padding: 2px 10px; cursor: pointer;
	white-space: nowrap;
}
.step-chip.active { color: #ff6600; border-color: #ff6600; }
.step-chip.done { color: #12e049; border-color: #12e04944; }

.wizard-carousel { flex: 1; background: transparent; min-height: 0; }
.slide { padding: 20px 24px; overflow-y: auto; }
.slide-title { color: #ff6600; font-size: 1.1em; letter-spacing: 1px; margin-bottom: 4px; }
.fg-links-head { color: #ff8c3a; font-size: 0.8em; letter-spacing: 1px; margin: 4px 0; }
.fg-links-note { color: #7a7a7a; font-size: 0.85em; letter-spacing: 0; }
.recruit-settings { display: flex; flex-direction: column; gap: 10px; margin-top: 8px; padding: 10px 12px; border: 1px solid #ff660030; border-radius: 4px; }
.recruit-row { display: flex; align-items: center; gap: 12px; }
.recruit-label { color: #ff8c3a; font-size: 0.8em; letter-spacing: 1px; min-width: 90px; }
.recruit-help { color: #ff8c3a; cursor: help; }
.slide-sub { color: #789; font-size: 0.8em; margin-bottom: 16px; }
.loading-note { color: #567; font-style: italic; padding: 20px; }
.content-col-title { color: #ff6600; font-size: 0.9em; letter-spacing: 1px; margin-bottom: 8px; }

/* Module step: one-card-at-a-time carousel with big art */
.module-slide { display: flex; flex-direction: column; min-height: 0; }
.module-carousel {
	display: flex;
	align-items: stretch;
	gap: 12px;
	flex: 1 1 auto;   /* fill the slide so the image is as big as possible */
	min-height: 0;
	margin-top: 4px;
}
.mod-nav-btn {
	flex: 0 0 auto;
	width: 46px;
	background: #0a1418;
	border: 1px solid #00ffea40;
	border-radius: 6px;
	color: #00ffea;
	font-size: var(--fs-large);
	cursor: pointer;
	transition: all 0.15s;
}
.mod-nav-btn:hover { border-color: #00ffea; background: #00ffea10; }
.module-card-big {
	flex: 1 1 auto;
	min-width: 0;
	display: flex;
	flex-direction: column;
	border: 1px solid #234;
	border-radius: 8px;
	background: #0a1418;
	overflow: hidden;
	text-align: center;
	transition: border-color 0.12s ease, box-shadow 0.12s ease;
}
.module-card-big.selected { border-color: #ff6600; box-shadow: 0 0 10px #ff660030; }
.module-card-big-img {
	flex: 1 1 auto;
	min-height: 0;
	width: 100%;
	object-fit: contain;
	background: #050a0e;
	padding: 6px;
}
.module-card-body { flex: 0 0 auto; padding: 8px 16px 14px; }
.module-name { color: #ff6600; font-size: 1.31em; font-weight: 600; padding: 10px 8px 4px; flex-shrink: 0; }
.module-tagline { color: #00ffea; font-size: 1em; margin: 4px 0; font-style: italic; }
.module-blurb { color: #89a; font-size: 0.98em; line-height: 1.4; }
.module-defaults { color: #6c8; font-size: 0.88em; margin-top: 8px; opacity: 0.8; }

.module-carousel-controls { display: flex; justify-content: center; margin: 12px 0 6px; }
.module-select-btn {
	background: #0a1810;
	border: 1px solid #51ff0060;
	border-radius: 3px;
	color: #51ff00;
	font-family: inherit;
	font-size: 0.9em;
	letter-spacing: 2px;
	min-width: 220px;
	padding: 10px 24px;
	cursor: pointer;
	transition: all 0.15s;
}
.module-select-btn:hover { border-color: #51ff00; background: #51ff0015; box-shadow: 0 0 8px #51ff0030; }
.module-select-btn.selected { color: #051005; background: #51ff00; border-color: #51ff00; }

.module-dots { display: flex; justify-content: center; gap: 8px; padding: 4px 0; }
.module-dot {
	width: 10px; height: 10px; border-radius: 50%;
	border: 1px solid #00ffea50; cursor: pointer; transition: all 0.15s;
}
.module-dot:hover { border-color: #00ffea; }
.module-dot.active { background: #00ffea; border-color: #00ffea; }
.module-dot.chosen { border-color: #51ff00; background: #51ff0080; }
.module-dot.chosen.active { background: #51ff00; }

.review-grid { display: flex; flex-direction: column; gap: 8px; max-width: 640px; }
.review-line { display: flex; justify-content: space-between; gap: 16px; border-bottom: 1px solid #123; padding: 6px 0; }
.review-line span { color: #789; font-size: 0.85em; }
.review-line b { color: #00ffea; font-size: 0.85em; text-align: right; }
.review-line b.review-url { word-break: break-all; min-width: 0; }

.dialog-actions {
	display: flex; justify-content: space-between; align-items: center;
	padding: 12px; background: #0a1418;
}
.nav-right { display: flex; gap: 8px; align-items: center; }
.creating-loader {
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 10px;
	min-width: 130px;
	color: #51ff00;
	font-family: 'Share Tech Mono', 'Courier New', monospace;
	font-size: 0.9em;
	letter-spacing: 2px;
}
</style>
